import dayjs from "dayjs";

export const STATUS = {
  BANNED: "banned",
  DELETED: "deleted",
  FREE: "free",
  FREE_FUTURE: "freeFuture",
  ACTIVE: "active",
  INACTIVE: "inactive",
  GRACE_PERIOD: "gracePeriod",
  NEVER_PAID: "neverPaid",
  REGISTERED: "registered",
  MERGED: "merged",
};

/**
 * The first not-yet-started, non-free subscription, if any.
 * `person` must expose a `subscriptions` array.
 */
export function getFutureSub(person) {
  const now = dayjs();
  return person?.subscriptions?.filter((x) => {
    return x.validFrom !== null && dayjs(x.validFrom).isAfter(now) && !x.isFree;
  })?.[0];
}

/**
 * Derived next payment date for an active, open-ended subscription.
 */
export function getNextPaymentDate(activeSubscription) {
  if (
    activeSubscription &&
    !activeSubscription.validUntil &&
    activeSubscription.billingCycleDate
  ) {
    return dayjs(new Date(activeSubscription.billingCycleDate)).add(
      activeSubscription.transactionCount,
      "month"
    );
  }
  return null;
}

/**
 * Pure port of the `status` getter previously living in
 * stores/admin/professional.js. Works with any person object that exposes the
 * subscription/account fields (both `PersonFragment` and the private-data
 * query are supersets of what is read here).
 */
export function getPersonStatus(person) {
  if (!person) {
    return null;
  }
  if (person.mergedPerson) {
    return STATUS.MERGED;
  }
  if (person.banned) {
    return STATUS.BANNED;
  }
  if (person.deleted) {
    return STATUS.DELETED;
  }
  if (person.emailConfirmationDate === null) {
    return STATUS.REGISTERED;
  }
  const activeSubscription = person.activeSubscription;
  if (activeSubscription) {
    if (activeSubscription.isFree) {
      return STATUS.FREE;
    }
    if (!activeSubscription.validUntil && activeSubscription.billingCycleDate) {
      const nextPaymentDate = dayjs(
        new Date(activeSubscription.billingCycleDate)
      ).add(activeSubscription.transactionCount, "month");
      if (dayjs().isAfter(nextPaymentDate)) {
        return STATUS.GRACE_PERIOD;
      }
    }
    return STATUS.ACTIVE;
  }
  if (person.futureFreeSubscription) {
    return STATUS.FREE_FUTURE;
  }
  if (!person.lastPaidSubscription) {
    return STATUS.NEVER_PAID;
  }
  return STATUS.INACTIVE;
}

export function usePersonStatus() {
  return {
    STATUS,
    getPersonStatus,
    getFutureSub,
    getNextPaymentDate,
  };
}
